import { motion } from "framer-motion";

/**
 * Split-line reveal animator — animates each supplied line with a masked slide-up.
 * lines: array of ReactNodes (each line rendered as its own block).
 */
export function KineticHeadline({
  lines,
  className = "",
  lineClassName = "",
  delay = 0,
  stagger = 0.09,
  as = "h1",
  ...rest
}) {
  const Tag = motion[as] || motion.h1;
  return (
    <Tag className={className} {...rest}>
      {lines.map((line, i) => (
        <span
          key={i}
          className={`block overflow-hidden ${lineClassName}`}
          style={{ paddingBottom: "0.1em" }}
        >
          <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{
              duration: 0.9,
              delay: delay + i * stagger,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

/**
 * Reveals children on scroll into view (once), with a subtle rise-and-fade.
 */
export function ScrollReveal({
  children,
  className = "",
  delay = 0,
  y = 32,
  duration = 0.85,
  once = true,
  amount = 0.25,
  ...rest
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount }}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/**
 * A small word-by-word reveal for paragraphs / subtitles.
 */
export function WordReveal({ text, className = "", delay = 0, stagger = 0.03 }) {
  const words = text.split(" ");
  return (
    <span className={className} aria-label={text}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden" aria-hidden>
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{
              duration: 0.7,
              delay: delay + i * stagger,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </span>
  );
}
